import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { finalize, lastValueFrom, take } from 'rxjs';
import { ConsentService } from 'src/app/api/services/consent.service';
import { PriceService } from 'src/app/api/services/price.service';
import { PathType } from 'src/app/core/enums/path-type.enum';
import { Civilite } from 'src/app/core/models/form-state.model';
import { Stepper } from 'src/app/core/models/stepper.model';
import { State } from 'src/app/core/state/core.state';
import { Resource } from 'src/app/resources/resource';
import { RATE_PATH, ROOT_PATH } from '../constants/route.constants';
import { ApiRequestBodyUtils } from '../utils/apiRequestBodyUtils';
import { ApiResponseBodyUtils } from '../utils/apiResponseBodyUtils';

import * as fromAddress from 'src/app/core/state/address';
import * as fromContext from 'src/app/core/state/context';
import * as fromHousing from 'src/app/core/state/housing';
import * as fromInfo from 'src/app/core/state/info';
import * as fromInsured from 'src/app/core/state/insured';
import * as fromOffer from 'src/app/core/state/offer-customization';
import * as fromPath from 'src/app/core/state/path';
import * as fromService from 'src/app/core/state/service';
import * as fromSubscriber from 'src/app/core/state/subscriber';
import * as fromTarification from 'src/app/core/state/tarification';
@Injectable({
  providedIn: 'root'
})
export class SubscriberStepService {
  constructor(
    protected store: Store<State>,
    private priceService: PriceService,
    private consentService: ConsentService,
    private router: Router
  ) {}

  async confirmStep(
    resource: Resource,
    brand: string,
    civility: Civilite,
    lastName: string,
    firstName: string,
    email: string,
    phonePrefix: string,
    phone: string,
    birthdayIso: string,
    birthdayPlace: string,
    birthdayCountry: string,
    path: string,
    getProductPricesFinalize?: () => void
  ) {
    this.store.dispatch(
      fromSubscriber.patchSubscriber({
        payload: {
          Civilite: civility,
          Nom: lastName,
          Prenom: firstName,
          Email: email,
          Indicatif: phone ? phonePrefix : '',
          Telephone: phone,
          DateNaissance: birthdayIso ?? undefined,
          LieuNaissance: birthdayPlace ?? '',
          PaysNaissance: birthdayCountry
        }
      })
    );

    // Retrieve context
    const context = await lastValueFrom(
      this.store.select(fromContext.selectContextId).pipe(take(1))
    );

    // Retrieve product
    const product = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));

    // Retrieve address
    const stateAddress = await lastValueFrom(
      this.store.select(fromAddress.selectAddress).pipe(take(1))
    );

    // Retrieve housing informations
    const stateHousing = await lastValueFrom(
      this.store.select(fromHousing.selectHousing).pipe(take(1))
    );

    // Retrieve selected formule informations
    const stateOffer = await lastValueFrom(
      this.store.select(fromOffer.selectOfferCustomization).pipe(take(1))
    );

    const stateService = await lastValueFrom(
      this.store.select(fromService.selectService).pipe(take(1))
    );

    // get LessorName if
    const lessorName = stateService.saisieChoixServices.EnvoiAttestationBailleur?.BailleurPrive
      ?.NomBailleur
      ? stateService.saisieChoixServices.EnvoiAttestationBailleur?.BailleurPrive?.NomBailleur
      : stateService.saisieChoixServices.EnvoiAttestationBailleur?.BailleurSocial?.NomBailleur
      ? stateService.saisieChoixServices.EnvoiAttestationBailleur?.BailleurSocial?.NomBailleur
      : null;

    const isHousingEtudiante = path === PathType.HOUSING_ETUDIANTE;
    let saisieInsured = null;
    if (isHousingEtudiante) {
      saisieInsured = await lastValueFrom(
        this.store.select(fromInsured.selectInsured).pipe(take(1))
      );
    }
    const coverageDurationInMonths = saisieInsured === null ? null : saisieInsured.duration;

    const body = ApiRequestBodyUtils.GenerateTarifCreateRequestBody(
      context!,
      product,
      brand,
      stateAddress.adresseType,
      stateAddress.addresseSelection,
      stateAddress.addresseManuelle,
      stateAddress.adresseEsh,
      stateHousing.SaisieHabitation,
      {
        ...stateOffer.SaisieFormule,
        FormuleSelectionnee: undefined // Recalculate all formules
      },
      stateOffer.FormuleRecommandee!,
      coverageDurationInMonths,
      email,
      lessorName
    );

    let consent = (
      await lastValueFrom(this.store.select(fromSubscriber.selectSubscriber).pipe(take(1)))
    ).subscriber?.Consentement;

    let getProductPriceCall = this.priceService.GetProductPrices(body);

    if (getProductPricesFinalize)
      getProductPriceCall = getProductPriceCall.pipe(finalize(getProductPricesFinalize));

    getProductPriceCall.subscribe({
      next: res => {
        this.store.dispatch(
          fromSubscriber.changeValidationStatus({
            isValid: true
          })
        );

        // Store returned informations about formules in the state
        const tarifInfos = ApiResponseBodyUtils.ExtractTarifResponseBody(res, path);
        this.store.dispatch(
          fromTarification.patchTarification({
            payload: tarifInfos
          })
        );

        // Send consent informations for HM case
        if (consent && brand === 'HM') {
          const bodyConsent = ApiRequestBodyUtils.GeneratePostConsentRequestBody(
            context!,
            consent.IdLibelle,
            consent.VoiesConsentement
          );
          this.consentService.PostConsentement(bodyConsent).subscribe({
            error: error => {
              console.error("Une erreur s'est produite :", error);
            },
            complete: () => {
              this.router.navigate([`${ROOT_PATH}${RATE_PATH}`]);
            }
          });
        } else {
          this.router.navigate([`${ROOT_PATH}${RATE_PATH}`]);
        }
      },
      error: error => {
        console.error("Une erreur s'est produite :", error);
      }
    });

    await this.updateStepper(resource);
  }

  async updateStepper(resource: Resource) {
    let stepper = await lastValueFrom(this.store.select(fromInfo.selectStepper).pipe(take(1)));
    var tmpStepper = JSON.parse(JSON.stringify(stepper)) as Stepper;

    let title = tmpStepper.devis.titles.find(
      (x: { libelle: string }) => x.libelle == resource.stepper.offer
    );

    let subtitleS = title?.subtitles.find(
      (x: { url: string }) => x.url == resource.stepper.subscriber
    );

    if (subtitleS) subtitleS.isValid = true;

    let subtitleN = title?.subtitles.find((x: { url: string }) => x.url == resource.stepper.rate);

    if (subtitleN) subtitleN.isActive = true;

    this.store.dispatch(fromInfo.updateStepper({ payload: tmpStepper }));
  }
}
